import React from 'react';
import styled from 'styled-components/macro';
import {Button, FlexColumn, FlexRow, Icon, Modal, Text, ExpandableContainer, Frame} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import RichTextItem from '../richtext_item';
import {useIntl} from 'react-intl';
import Util from '../../forecast-app/shared/util/util';
import {DeliverableEdges, Deliverable, BaselineItem} from './BaselineTypes';

type ManageDeliverablesModalProps = {
	closeModal: () => void;
	deliverables: DeliverableEdges;
	projectId: string;
	baselineItems?: BaselineItem[];
};

export const RichTextWrapper = styled.div`
	overflow: hidden;
	.editor {
		padding: 8px 0 0 0;
		margin: 0;
		.public-DraftEditor-content {
			min-height: 0px;
		}
	}
	.DraftEditor-editorContainer,
	.DraftEditor-root {
		position: unset;
		border-left-width: 0;
	}
`;

const ManageDeliverablesModal = ({
	closeModal,
	deliverables,
	projectId,
	baselineItems,
}: ManageDeliverablesModalProps): JSX.Element => {
	const intl = useIntl();

	const onEditClick = (deliverable: Deliverable) => {
		closeModal();
		showModal({
			type: MODAL_TYPE.DELIVERABLE_EDITOR_MODAL,
			deliverables,
			deliverable,
			action: 'edit',
			projectId,
			baselineItems,
		});
	};

	const onCreateClick = () => {
		closeModal();
		showModal({
			type: MODAL_TYPE.DELIVERABLE_EDITOR_MODAL,
			deliverables,
			action: 'create',
			projectId,
			baselineItems,
		});
	};

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} setHeight={true}>
			<Modal.Title>{intl.formatMessage({id: 'deliverables.guide_section.deliverables.button'})} </Modal.Title>
			<Modal.Content>
				<Button emphasis={'medium'} onPress={onCreateClick}>
					{intl.formatMessage({id: 'deliverables.manage.modal.add_new_deliverable'})}
				</Button>
				<FlexColumn>
					<FlexRow>
						<Icon icon="deliverable"></Icon>
						<Text>{`Deliverables (${deliverables.length})`}</Text>
					</FlexRow>
					{deliverables.length === 0 ? (
						<Frame height={'s'} centerContent={true}>
							<Text size={'2'}>{intl.formatMessage({id: 'deliverables.manage.modal.no_deliverables'})}</Text>
						</Frame>
					) : (
						<FlexColumn>
							{deliverables.map(deliverable => (
								<ExpandableContainer
									primaryText={deliverable?.node?.name || ''}
									secondaryText={
										deliverable?.node?.description
											? Util.ConvertDraftJsToPlainText(deliverable?.node?.description)
											: ''
									}
									onActionClick={() => deliverable?.node && onEditClick(deliverable?.node)}
									isExpandable={true}
								>
									<RichTextWrapper>
										<RichTextItem
											alwaysShowControls={false}
											readOnly={true}
											text={deliverable?.node?.description}
										/>
									</RichTextWrapper>
								</ExpandableContainer>
							))}
						</FlexColumn>
					)}
				</FlexColumn>
			</Modal.Content>
			{deliverables.length > 0 && (
				<Modal.Footer>
					{/** Hack to enable scrollbar if content is too long */}
					<></>
				</Modal.Footer>
			)}
		</Modal>
	);
};

export default ManageDeliverablesModal;
